import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, ArrowLeft, Check, X, RotateCcw, Trophy } from "lucide-react";
import { Reveal, Eyebrow } from "../components/Reveal";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";

const AGE_GROUPS = ["Under 12", "12–14", "15–17", "18+"];
const LETTERS = ["A", "B", "C", "D", "E", "F"];

const fmtTime = (ms) => {
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s}s`;
  return `${Math.floor(s / 60)}m ${s % 60}s`;
};

const Quiz = () => {
  const [quiz, setQuiz] = useState(undefined); // undefined = loading, null = none live
  const [stage, setStage] = useState("intro"); // intro | playing | done
  const [name, setName] = useState("");
  const [ageGroup, setAgeGroup] = useState("");
  const [qi, setQi] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [picked, setPicked] = useState(null); // {choice, correct, correct_index}
  const [startedAt, setStartedAt] = useState(0);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/quiz/active").then((r) => setQuiz(r.data)).catch(() => setQuiz(null));
  }, []);

  const start = async () => {
    if (!name.trim()) return;
    setBusy(true);
    try {
      await api.post("/quiz/check-name", { name: name.trim() });
      setAnswers([]);
      setPicked(null);
      setQi(0);
      setStartedAt(Date.now());
      setStage("playing");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const pick = async (choice) => {
    if (picked || busy) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/quiz/${quiz.id}/answer`, { question_index: qi, choice });
      setPicked({ choice, correct: data.correct, correct_index: data.correct_index });
      setAnswers((a) => {
        const n = [...a];
        n[qi] = choice;
        return n;
      });
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const next = () => {
    if (qi + 1 >= quiz.questions.length) {
      finish();
      return;
    }
    setQi(qi + 1);
    setPicked(null);
  };

  const finish = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/quiz/${quiz.id}/submit`, {
        name: name.trim(),
        age_group: ageGroup,
        time_ms: Date.now() - startedAt,
        answers,
      });
      setResult(data);
      setStage("done");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const playAgain = () => {
    setStage("intro");
    setResult(null);
    setPicked(null);
    setQi(0);
    setAnswers([]);
  };

  return (
    <div className="min-h-screen bg-cream dark:bg-background text-foreground">
      <Navbar />
      <main className="max-w-3xl mx-auto px-4 sm:px-8 py-12 md:py-16">
        <Reveal>
          <Eyebrow>Every day · 10 questions · every age</Eyebrow>
          <h1 className="mt-3 font-display text-4xl sm:text-5xl font-bold tracking-tighter">
            The Morbi <span className="font-serif italic font-medium marker-highlight">quiz</span>
          </h1>
          <p className="mt-3 text-muted-foreground max-w-xl">
            Right or wrong, no points — but the leaderboard remembers who was the sharpest and
            the quickest. Finish fast, finish correct.
          </p>
        </Reveal>

        {quiz === undefined ? (
          <div className="mt-12 grid place-items-center py-16 text-muted-foreground" data-testid="quiz-loading">
            <Loader2 className="h-6 w-6 animate-spin" />
          </div>
        ) : quiz === null ? (
          <div className="mt-10 rounded-3xl border-2 border-dashed border-foreground/15 p-10 text-center" data-testid="quiz-empty">
            <p className="font-display font-bold text-lg">No quiz right now</p>
            <p className="text-sm text-muted-foreground mt-1">
              I'll post the next one from the Owner's room — come back soon!
            </p>
            <Link to="/" className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-pitch dark:text-turf hover:opacity-75 transition-opacity">
              <ArrowLeft className="h-4 w-4" /> Back to the site
            </Link>
          </div>
        ) : stage === "intro" ? (
          <Reveal delay={0.05}>
            <div className="mt-8 rounded-3xl bg-card border border-foreground/10 p-6 md:p-8 shadow-sm" data-testid="quiz-intro-card">
              <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-pitch dark:text-turf">Today's quiz</p>
              <h2 className="mt-2 font-display text-2xl font-bold tracking-tight">{quiz.title}</h2>
              <p className="mt-1 text-sm text-muted-foreground">{quiz.question_count} questions · every age group can play</p>

              <div className="mt-6">
                <label htmlFor="quiz-name" className="text-sm font-semibold">Your name (for the leaderboard)</label>
                <input
                  id="quiz-name"
                  data-testid="quiz-name-input"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  maxLength={24}
                  placeholder="e.g. Ansh"
                  className={`mt-2 ${"w-full rounded-2xl border-2 border-foreground/10 bg-background px-4 py-3 text-sm focus:outline-none focus:border-pitch dark:focus:border-turf"}`}
                />
              </div>

              <div className="mt-5">
                <p className="text-sm font-semibold">Your age group (shows as a badge on the leaderboard)</p>
                <div className="mt-2 flex flex-wrap gap-2">
                  {AGE_GROUPS.map((g) => (
                    <button
                      key={g}
                      data-testid="quiz-age-option"
                      onClick={() => setAgeGroup(ageGroup === g ? "" : g)}
                      className={`px-4 py-2 rounded-full border-2 text-sm font-semibold transition-all ${
                        ageGroup === g
                          ? "bg-pitch text-cream border-pitch dark:bg-turf dark:text-background dark:border-turf"
                          : "border-foreground/10 bg-background hover:border-pitch/40 dark:hover:border-turf/50"
                      }`}
                    >
                      {g}
                    </button>
                  ))}
                </div>
              </div>

              <button
                onClick={start}
                disabled={!name.trim() || busy}
                data-testid="quiz-start-btn"
                className="mt-7 h-12 px-8 rounded-full bg-pitch text-cream font-semibold hover:bg-pitch-deep disabled:opacity-40 transition-colors dark:bg-turf dark:text-background"
              >
                Start the quiz
              </button>
            </div>
          </Reveal>
        ) : stage === "playing" ? (
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }} className="mt-8">
            <div className="flex items-center justify-between">
              <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-pitch dark:text-turf" data-testid="quiz-progress">
                Question {qi + 1} of {quiz.questions.length}
              </p>
              <div className="h-2 w-32 sm:w-48 rounded-full bg-foreground/10 overflow-hidden">
                <motion.div
                  className="h-full bg-flame rounded-full"
                  animate={{ width: `${((qi + (picked ? 1 : 0)) / quiz.questions.length) * 100}%` }}
                  transition={{ duration: 0.4 }}
                />
              </div>
            </div>

            <div className="mt-4 rounded-3xl bg-card border border-foreground/10 p-6 md:p-8 shadow-sm" data-testid="quiz-question-card">
              <h2 className="font-display text-xl sm:text-2xl font-bold tracking-tight leading-snug">
                {quiz.questions[qi].text}
              </h2>
              <div className="mt-5 grid gap-3">
                {quiz.questions[qi].options.map((opt, oi) => {
                  const isCorrect = picked && oi === picked.correct_index;
                  const isWrongPick = picked && oi === picked.choice && !picked.correct;
                  return (
                    <button
                      key={oi}
                      data-testid={`quiz-option-${oi}`}
                      onClick={() => pick(oi)}
                      disabled={!!picked || busy}
                      className={`flex items-center gap-3 rounded-2xl border-2 px-4 py-3.5 text-left text-sm sm:text-base font-medium transition-all ${
                        isCorrect
                          ? "border-emerald-500 bg-emerald-500/10"
                          : isWrongPick
                            ? "border-flame bg-flame/10"
                            : "border-foreground/10 bg-background hover:border-pitch/50 dark:hover:border-turf/60 disabled:opacity-60"
                      }`}
                    >
                      <span className={`grid place-items-center h-8 w-8 rounded-full font-mono text-xs shrink-0 ${
                        isCorrect ? "bg-emerald-500 text-white" : isWrongPick ? "bg-flame text-white" : "bg-foreground/10"
                      }`}>
                        {isCorrect ? <Check className="h-4 w-4" /> : isWrongPick ? <X className="h-4 w-4" /> : LETTERS[oi]}
                      </span>
                      {opt}
                    </button>
                  );
                })}
              </div>

              {picked && (
                <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="mt-5 flex items-center justify-between gap-4 flex-wrap">
                  <p className={`text-sm font-semibold ${picked.correct ? "text-emerald-600 dark:text-emerald-400" : "text-flame"}`} data-testid="quiz-verdict">
                    {picked.correct ? "Correct — well played!" : "Wrong — good try though!"}
                  </p>
                  <button
                    onClick={next}
                    data-testid="quiz-next-btn"
                    className="h-11 px-7 rounded-full bg-pitch text-cream font-semibold hover:bg-pitch-deep transition-colors dark:bg-turf dark:text-background"
                  >
                    {qi + 1 >= quiz.questions.length ? "See my result" : "Next question"}
                  </button>
                </motion.div>
              )}
            </div>
          </motion.div>
        ) : (
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }} className="mt-8">
            <div className="rounded-3xl bg-pitch text-cream p-6 md:p-8" data-testid="quiz-result-card">
              <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-turf">Your result</p>
              <h2 className="mt-2 font-display text-3xl font-bold tracking-tight">
                {result.correct}/{result.total} correct
              </h2>
              <p className="mt-2 text-sm text-cream/80">No points, just bragging rights — the leaderboard keeps the best score for your name.</p>
            </div>

            <div className="mt-6 rounded-3xl bg-card border border-foreground/10 p-6 md:p-8 shadow-sm" data-testid="quiz-leaderboard">
              <h3 className="flex items-center gap-2 font-display text-xl font-bold tracking-tight">
                <Trophy className="h-5 w-5 text-flame" /> Leaderboard — sharpest and fastest
              </h3>
              {result.leaderboard.length === 0 ? (
                <p className="mt-4 text-sm text-muted-foreground">No entries yet — you're the first!</p>
              ) : (
                <ol className="mt-5 space-y-2">
                  {result.leaderboard.map((r, i) => (
                    <li key={i} data-testid="quiz-leaderboard-row" className="flex items-center gap-3 rounded-2xl bg-background border border-foreground/10 px-4 py-3">
                      <span className={`grid place-items-center h-8 w-8 rounded-full font-mono text-xs shrink-0 ${i === 0 ? "bg-flame text-white font-bold" : "bg-foreground/10"}`}>
                        {String(i + 1).padStart(2, "0")}
                      </span>
                      <span className="font-semibold text-sm flex-1 truncate">{r.name}</span>
                      {r.age_group && <span className="hidden sm:inline font-mono text-[10px] uppercase tracking-wider text-muted-foreground">{r.age_group}</span>}
                      <span className="font-mono text-xs text-pitch dark:text-turf">{r.correct}/10</span>
                      <span className="font-mono text-xs text-muted-foreground">{fmtTime(r.time_ms)}</span>
                    </li>
                  ))}
                </ol>
              )}
            </div>

            <div className="mt-6 flex flex-wrap gap-3">
              <button onClick={playAgain} data-testid="quiz-play-again" className="inline-flex items-center gap-2 h-12 px-7 rounded-full bg-pitch text-cream font-semibold hover:bg-pitch-deep transition-colors dark:bg-turf dark:text-background">
                <RotateCcw className="h-4 w-4" /> Play again
              </button>
              <Link to="/" className="inline-flex items-center gap-2 h-12 px-7 rounded-full border-2 border-foreground/10 font-semibold hover:bg-muted transition-colors">
                <ArrowLeft className="h-4 w-4" /> Back to the site
              </Link>
            </div>
          </motion.div>
        )}
      </main>
      <Footer />
    </div>
  );
};

export default Quiz;
